import { FilterBar } from "@/components/FilterBar";
import { EventHeader } from "@/components/eventHeader";
import { SearchIcon } from "@/components/icons";
import { MainContainer } from "@/components/mainContainer";
import {
  Box,
  Button,
  Divider,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import * as Inputs from "../../../components/inputs";
import * as Fonts from "../../../components/texts";
import * as T from "../../../components/table";
import { SendEmail } from "@/components/icons/email";
import { OutlineFileDownload } from "@/components/icons/download";
import { OutlineEditNote } from "@/components/icons/edit";
import { useParams } from "react-router-dom";
import {
  EventByIdDto,
  EventClientFilterDto,
  EventClientIndexDto,
  EventClientIndexFilterDto,
} from "../types";
import { useEvent } from "@/hooks/useEvent";
import { useEventClient } from "@/hooks/useEventClient";
import { useCustomToast } from "@/hooks/useToast";
import { useForm } from "react-hook-form";
import LoadingOverlay from "@/components/overlay";
import { DeleteIcon } from "@/components/icons/delete";

const initialFilter: EventClientFilterDto = {
  name: "",
  email: "",
  cpf: "",
};

type GuestFormData = {
  guestId?: number;
  eventClientId: number;
  clientName?: string;
  entranceId: number;
  name: string;
  email: string;
  phone: string;
};

export const PickupReport = () => {
  const [filter, setFilter] =
    useState<EventClientIndexFilterDto>(initialFilter);
  const { id: eventId } = useParams<{ id: string }>();
  const { getEventById } = useEvent();
  const [page] = useState<number>(1);
  const {
    getEventClients,
    updateClientGuest,
    addClientGuest,
    sendInviteMail,
    donwloadInvite,
    deleteGuest,
  } = useEventClient();
  const [event, setEvent] = useState<EventByIdDto>();
  const [graduates, setGraduates] = useState<EventClientIndexDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { customToast } = useCustomToast();
  const [filterType, setFilterType] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Track whether the modal is in "add" or "edit" mode
  const [isAddMode, setIsAddMode] = useState<boolean>(false);

  // Track entrances scoped to the currently selected graduate (for the modal)
  const [modalEntrances, setModalEntrances] = useState<any[]>([]);

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<GuestFormData>({});

  useEffect(() => {
    getEventById(Number(eventId))
      .then((response) => setEvent(response.data))
      .catch((error: any) =>
        customToast("error", [error.response.data.message].join(", "))
      );
  }, []);

  const callbackGraduates = useCallback(() => {
    setIsLoading(true);
    getEventClients({
      page,
      perPage: 999999,
      eventId: Number(eventId),
      ...filter,
    })
      .then((response) => {
        setGraduates(response.data.data);
      })
      .catch((error: any) => {
        customToast("error", error.response?.data?.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filter]);

  useEffect(() => {
    callbackGraduates();
  }, [callbackGraduates]);

  const onSubmit = (data: GuestFormData) => {
    const payload = {
      entranceId: data.entranceId,
      name: data.name,
      email: data.email,
      phone: data.phone,
    };

    const request = isAddMode
      ? addClientGuest(data.eventClientId, payload)
      : updateClientGuest(data.guestId!, payload);

    request
      .then(() => {
        customToast(
          "success",
          isAddMode
            ? "Convidado adicionado com sucesso!"
            : "Convidado atualizado com sucesso!"
        );
      })
      .catch((error: any) => {
        customToast("error", [error.response.data.message].join(", "));
      })
      .finally(() => {
        onClose();
        callbackGraduates();
        reset({
          guestId: undefined,
          eventClientId: undefined,
          clientName: "",
          entranceId: undefined,
          name: "",
          email: "",
          phone: "",
        });
      });
  };

  const sendInviteEmail = (id: number) => {
    setIsLoading(true);
    sendInviteMail(id)
      .then(() => {
        customToast("success", "Convite enviado com sucesso");
      })
      .catch((error: any) => {
        customToast("error", error.response?.data?.message);
      })
      .finally(() => {
        callbackGraduates();
        setIsLoading(false);
      });
  };

  const handleDeleteGuest = (id: number) => {
    deleteGuest(id)
      .then(() => {
        customToast("success", "Convidado excluido com sucesso");
      })
      .catch((error: any) => {
        customToast("error", error.response?.data?.message);
      })
      .finally(() => {
        callbackGraduates();
      });
  };

  const handleEditGuest = (
    guest: any,
    graduateId: number,
    clientName: string,
    entrances: any[]
  ) => {
    setIsAddMode(false);
    setModalEntrances(entrances);
    reset({
      guestId: guest.id,
      eventClientId: graduateId,
      clientName: clientName,
      entranceId: guest.entranceId,
      name: guest.name,
      email: guest.email,
      phone: guest.phone,
    });
    onOpen();
  };

  // Opens the modal in "add" mode for a specific graduate
  const handleAddGuest = (
    graduateId: number,
    clientName: string,
    entrances: any[]
  ) => {
    setIsAddMode(true);
    setModalEntrances(entrances);
    reset({
      guestId: undefined,
      eventClientId: graduateId,
      clientName: clientName,
      entranceId: undefined,
      name: "",
      email: "",
      phone: "",
    });
    onOpen();
  };

  const handleDownloadInvite = (id: number) => {
    donwloadInvite(id)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `convite_${id}.png`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        customToast("success", "Convite baixado!");
      })
      .catch((error: any) => {
        customToast(
          "error",
          error.response?.data?.message || "Erro ao baixar convite"
        );
      })
      .finally(() => {
        callbackGraduates();
      });
  };

  const filters = [
    {
      field: (
        <HStack w="30%" justifyContent="flex-end" key={1}>
          <Text
            fontFamily="Inter"
            fontStyle="normal"
            fontWeight="400"
            fontSize="14px"
            lineHeight="17px"
            color="neutral.500"
          >
            Buscar por:
          </Text>
          <Select
            w="70%"
            h={9}
            borderRadius={4}
            bg="neutral.0"
            onChange={(event) => {
              const selected = event.target.value;
              setFilterType(selected);
            }}
          >
            <option value="">Selecione</option>
            <option value="name">Nome do Cliente</option>
            <option value="email">E-mail</option>
            <option value="cpf">CPF</option>
          </Select>
        </HStack>
      ),
    },
    {
      field: (
        <InputGroup alignItems="center" key={2}>
          <InputLeftElement pointerEvents="none" top="" left={1}>
            <SearchIcon />
          </InputLeftElement>
          <Inputs.SimpleText
            placeholder="O que você procura?"
            w="100%"
            h={9}
            borderRadius={4}
            textIndent="35px"
            bg="neutral.0"
            onChange={(event) => {
              const value = event.target.value;
              setFilter({ ...filter, [filterType]: value });
            }}
          />
        </InputGroup>
      ),
    },
  ];

  return (
    <>
      <EventHeader eventName={event?.name} />
      <MainContainer
        height={isLoading || graduates.length < 2 ? undefined : "100%"}
      >
        {isLoading && <LoadingOverlay />}
        <FilterBar title="Relatório de Retirada" filters={filters} />
        <Stack p={5} mb={"0 !important"} w="100%">
          <Box bgColor="neutral.100" borderRadius={5} p={3}>
            <HStack w="100%" spacing={5} mt={5}>
              <Stack w="75%">
                <Text
                  fontFamily="Inter"
                  fontStyle="normal"
                  fontWeight="600"
                  fontSize="24px"
                  lineHeight="31px"
                  color="neutral.600"
                >
                  Informações do Evento
                </Text>
              </Stack>
            </HStack>
            <Divider h={2} />
            <Stack p={2}>
              <Stack key={event?.id}>
                <HStack w="100%" spacing={5} mt={5}>
                  <HStack w="40%">
                    <Text fontWeight="semibold">Evento</Text>
                    <Text>{event?.name}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Tipo do Evento</Text>
                    <Text>{event?.type.name}</Text>
                  </HStack>
                </HStack>
                <HStack w="100%" spacing={5} mt={5}>
                  <HStack w="40%">
                    <Text fontWeight="semibold">Unidade</Text>
                    <Text>{event?.branch.name}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Data e Hora</Text>
                    <Text>
                      {event?.date ? new Date(event.date).toLocaleString() : ""}
                    </Text>
                  </HStack>
                </HStack>
                <HStack w="100%" spacing={5} mt={5}>
                  <HStack w="40%">
                    <Text fontWeight="semibold">Tipo do Pacote</Text>
                    <Text>{event?.team.name}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Local</Text>
                    <Text>{event?.local}</Text>
                  </HStack>
                </HStack>
              </Stack>
            </Stack>
          </Box>
        </Stack>
        <Stack p={5} mb={"0 !important"} w="100%">
          {graduates.map((graduate) => (
            <Box bgColor="neutral.300" borderRadius={5} p={3} key={graduate.id}>
              <TableContainer p={4}>
                <Table>
                  <Thead>
                    <Tr>
                      <Th>
                        <Fonts.TableTheadText>
                          Nome do Cliente
                        </Fonts.TableTheadText>
                      </Th>
                      <Th textAlign="center">
                        <Fonts.TableTheadText>E-mail</Fonts.TableTheadText>
                      </Th>
                      <Th textAlign="center">
                        <Fonts.TableTheadText>CPF</Fonts.TableTheadText>
                      </Th>
                      {graduate?.entrance?.map((entranceHeaderType) => (
                        <Th
                          key={entranceHeaderType.eventEntranceId}
                          textAlign="center"
                        >
                          <Text
                            fontFamily="Inter"
                            fontWeight="900"
                            fontStyle="normal"
                            fontSize="11px"
                            lineHeight="23px"
                            color="#1a202c"
                          >
                            {entranceHeaderType.name}
                          </Text>
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    <T.Body key={graduate.id}>
                      <Td>
                        <Text>{graduate.name}</Text>
                      </Td>
                      <Td textAlign="center">
                        <Text>{graduate.email}</Text>
                      </Td>
                      <Td textAlign="center">
                        <Text>{graduate.cpf}</Text>
                      </Td>
                      {graduate?.entrance?.map((entranceType) => {
                        const totalUsed = (graduate.guests ?? []).filter(
                          (guest) => guest.entranceId === entranceType.id
                        ).length;
                        return (
                          <Td key={entranceType.id} textAlign="center">
                            <Text>
                              {totalUsed}/{entranceType.quantityInvitation}
                            </Text>
                          </Td>
                        );
                      })}
                    </T.Body>
                  </Tbody>
                </Table>
              </TableContainer>

              {/* ── Cabeçalho + botão "Adicionar Convidado" acima da tabela ── */}
              <HStack justifyContent="space-between" px={4} pt={3} pb={1}>
                <Fonts.TableTheadText>Convidados</Fonts.TableTheadText>
                <Button
                  size="sm"
                  color="white"
                  bgColor="secondary.500"
                  _hover={{ bgColor: "secondary.300" }}
                  onClick={() =>
                    handleAddGuest(
                      graduate.id,
                      graduate.name,
                      graduate.entrance
                    )
                  }
                >
                  + Adicionar Convidado
                </Button>
              </HStack>

              <TableContainer p={4}>
                <Table>
                  <Thead bgColor="neutral.200">
                    <Tr>
                      <Th>
                        <Fonts.TableTheadText>
                          Nome do Convidado
                        </Fonts.TableTheadText>
                      </Th>
                      <Th textAlign="center">
                        <Fonts.TableTheadText>E-mail</Fonts.TableTheadText>
                      </Th>
                      <Th textAlign="center">
                        <Fonts.TableTheadText>Telefone</Fonts.TableTheadText>
                      </Th>
                      <Th textAlign="center">
                        <Fonts.TableTheadText>Lote</Fonts.TableTheadText>
                      </Th>
                      <Th textAlign="center">
                        <Fonts.TableTheadText>Retirada</Fonts.TableTheadText>
                      </Th>
                      <Th textAlign="center" w="10%">
                        <Fonts.TableTheadText>Ações</Fonts.TableTheadText>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {graduate.guests?.map((guest) => {
                      const entrance = graduate.entrance.find(
                        (e) => e.id === guest.entranceId
                      );
                      return (
                        <T.Body key={guest.id}>
                          <Th>
                            <Text>{guest.name}</Text>
                          </Th>
                          <Th textAlign="center">
                            <Text>{guest.email}</Text>
                          </Th>
                          <Th textAlign="center">
                            <Text>{guest.phone}</Text>
                          </Th>
                          <Th textAlign="center">
                            <Text>{entrance?.name}</Text>
                          </Th>
                          <Th textAlign="center">
                            <Text>
                              {guest.invitationSentAt
                                ? new Date(
                                    guest.invitationSentAt
                                  ).toLocaleString("pt-BR", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : "Ainda não Retirado"}
                            </Text>
                          </Th>
                          <Th w="10%" textAlign="center">
                            <Tooltip label="Enviar por E-mail">
                              <Button
                                variant="ghost"
                                onClick={() => sendInviteEmail(guest.id)}
                              >
                                <SendEmail color="blue" />
                              </Button>
                            </Tooltip>
                            <Tooltip label="Baixar">
                              <Button
                                variant="ghost"
                                onClick={() => handleDownloadInvite(guest.id)}
                              >
                                <OutlineFileDownload color="blue" />
                              </Button>
                            </Tooltip>
                            <Tooltip label="Editar">
                              <Button
                                variant="ghost"
                                onClick={() =>
                                  handleEditGuest(
                                    guest,
                                    graduate.id,
                                    graduate.name,
                                    graduate.entrance
                                  )
                                }
                              >
                                <OutlineEditNote color="blue" />
                              </Button>
                            </Tooltip>
                            <Tooltip label="Excluir">
                              <Button
                                variant="ghost"
                                onClick={() => handleDeleteGuest(guest.id)}
                              >
                                <DeleteIcon color="blue" />
                              </Button>
                            </Tooltip>
                          </Th>
                        </T.Body>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          ))}

          {/* ── Shared modal (add / edit) ── */}
          <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent bgColor="neutral.100">
              <form onSubmit={handleSubmit(onSubmit)}>
                <ModalHeader>
                  {isAddMode ? "Adicionar Convidado" : "Editar Convidado"}
                </ModalHeader>
                <ModalBody>
                  <Stack mt={1}>
                    <Text>Nome do cliente</Text>
                    <Input
                      disabled
                      bgColor="white"
                      borderColor={"black.500"}
                      {...register("clientName")}
                    />
                    {/* Hidden fields */}
                    <Input
                      hidden
                      bgColor="white"
                      {...register("eventClientId")}
                    />
                    {!isAddMode && (
                      <Input hidden bgColor="white" {...register("guestId")} />
                    )}
                  </Stack>
                  <Stack mt={5}>
                    <Text>Lote</Text>
                    <Select bgColor="white" {...register("entranceId")}>
                      <option value="">Selecione</option>
                      {modalEntrances.map((entrance) => (
                        <option key={entrance.id} value={entrance.id}>
                          {entrance.name}
                        </option>
                      ))}
                    </Select>
                    {errors?.entranceId?.message && (
                      <Text
                        fontFamily="Lato"
                        fontStyle="normal"
                        fontWeight="500"
                        fontSize="11px"
                        lineHeight="18px"
                        color="fail.500"
                      >
                        {errors.entranceId.message}
                      </Text>
                    )}
                  </Stack>
                  <Stack mt={5}>
                    <Text>Nome do Convidado</Text>
                    <Input
                      placeholder="Insira o nome do convidado"
                      bgColor="white"
                      {...register("name")}
                    />
                    {errors?.name?.message && (
                      <Text
                        fontFamily="Lato"
                        fontStyle="normal"
                        fontWeight="500"
                        fontSize="11px"
                        lineHeight="18px"
                        color="fail.500"
                      >
                        {errors.name.message}
                      </Text>
                    )}
                  </Stack>
                  <Stack mt={5}>
                    <Text>E-mail</Text>
                    <Input
                      type="email"
                      placeholder="Insira o e-mail do convidado"
                      bgColor="white"
                      {...register("email")}
                    />
                    {errors?.email?.message && (
                      <Text
                        fontFamily="Lato"
                        fontStyle="normal"
                        fontWeight="500"
                        fontSize="11px"
                        lineHeight="18px"
                        color="fail.500"
                      >
                        {errors.email.message}
                      </Text>
                    )}
                  </Stack>
                  <Stack mt={5}>
                    <Text>Telefone</Text>
                    <Input
                      placeholder="Insira o telefone do convidado"
                      bgColor="white"
                      {...register("phone")}
                    />
                    {errors?.phone?.message && (
                      <Text
                        fontFamily="Lato"
                        fontStyle="normal"
                        fontWeight="500"
                        fontSize="11px"
                        lineHeight="18px"
                        color="fail.500"
                      >
                        {errors.phone.message}
                      </Text>
                    )}
                  </Stack>
                </ModalBody>
                <ModalFooter justifyContent="space-between">
                  <Button
                    onClick={onClose}
                    w="48%"
                    border="1px"
                    borderColor="secondary.500"
                    bgColor="white"
                    color="secondary.500"
                    _hover={{
                      bgColor: "secondary.300",
                      borderColor: "secondary.300",
                      color: "white",
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    w="48%"
                    color="white"
                    bgColor="secondary.500"
                    _hover={{ bgColor: "secondary.300" }}
                  >
                    Confirmar
                  </Button>
                </ModalFooter>
              </form>
            </ModalContent>
          </Modal>
        </Stack>
      </MainContainer>
    </>
  );
};
